// eslint-disable-next-line import/no-unresolved, import/no-extraneous-dependencies
import { useLocation } from '@reach/router';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { AVERAGE_RATE_THRESHOLD } from '../../../constants';
import type { ExtendedFranchiseData } from './index';

const FranchiseLdJson = ({
    name,
    address,
    addressHidden,
    permalink,
    servicesProvidedReadable,
    wikipediaBasedGeographicAreas,
    averageRating,
    reviewCount,
    mainPhone,
}: ExtendedFranchiseData): JSX.Element|null => {
    const { pathname } = useLocation();

    if (!address || addressHidden) {
        return null;
    }

    const data = {
        '@context': 'https://schema.org',
        '@type': 'HomeAndConstructionBusiness',
        name,
        image: 'https://images.contentstack.io/v3/assets/blt0a0cb058815d4d96/blt9af1c0e2eaba012a/servpro_vehicle.jpeg?width=1200&auto=webp',
        address: {
            '@type': 'PostalAddress',
            streetAddress: `${address.address}${
                address.addressExt ? ` ${address.addressExt}` : ''
            }`,
            addressLocality: address.city,
            addressRegion: address.state,
            postalCode: address.postalCode,
        },
        telephone: mainPhone,
        url: `${process.env.URL || 'https://www.servpro.com'}${permalink}`,
        hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Property damage',
            itemListElement: [
                {
                    '@type': 'OfferCatalog',
                    name: 'Property damage cleanup',
                    itemListElement: servicesProvidedReadable
                        .filter(elem => !!elem.serviceLine)
                        .map(elem => ({
                            '@type': 'Offer',
                            itemOffered: {
                                '@type': 'Service',
                                name: elem.serviceLine,
                            },
                        })),
                },
            ],
        },
        makesOffer: {
            '@type': 'Offer',
            availability: 'InStock',
            itemOffered: {
                '@type': 'Service',
                serviceType: '24/7 water, fire, smoke, mold, and mildew damage cleanup',
                areaServed: (wikipediaBasedGeographicAreas?.places || []).map(({
                    city,
                    state,
                    latitude,
                    longitude,
                    wikipediaUrl,
                    locationType,
                }) => ({
                    '@type': locationType === 'City' ? 'City' : 'Place',
                    name: `${city}, ${state}`,
                    latitude,
                    longitude,
                    sameAs: wikipediaUrl,
                })),
                hasOfferCatalog: {
                    '@type': 'OfferCatalog',
                    name: 'Property damage',
                    itemListElement: [
                        {
                            '@type': 'OfferCatalog',
                            name: 'Property damage cleanup',
                            itemListElement: servicesProvidedReadable
                                .filter(elem => !!elem.serviceLine)
                                .map(elem => ({
                                    '@type': 'Offer',
                                    itemOffered: {
                                        '@type': 'Service',
                                        name: elem.serviceLine,
                                    },
                                })),
                        },
                    ],
                },
            },
        },
        openingHours: 'Mo,Tu,We,Th,Fr,Sa,Su 24 hours',
        openingHoursSpecification: [
            {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: [
                    'Monday',
                    'Tuesday',
                    'Wednesday',
                    'Thursday',
                    'Friday',
                    'Saturday',
                    'Sunday',
                ],
                opens: '00:00',
                closes: '23:59',
            },
        ],
        priceRange: '$$',
        areaServed: (wikipediaBasedGeographicAreas?.places || []).map(({
            city,
            state,
            latitude,
            longitude,
            wikipediaUrl,
        }) => ({
            '@type': 'City',
            name: `${city}, ${state}`,
            latitude,
            longitude,
            sameAs: wikipediaUrl,
        })),
        aggregateRating: (averageRating > AVERAGE_RATE_THRESHOLD && reviewCount > 0) ? {
            '@type': 'AggregateRating',
            itemReviewed: {
                '@type': 'Thing',
                name: `${process.env.URL || 'https://www.servpro.com'}${pathname}`,
            },
            ratingValue: averageRating,
            bestRating: 5,
            worstRating: 1,
            ratingCount: reviewCount,
        } : undefined,
    };

    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify(data)}
            </script>
        </Helmet>
    );
};

export default FranchiseLdJson;
